// ═══════════════════════════════════════════════════════════════════════════
// grainTargetPosition.js — POSITION -> TARGET RANGE -> GAP sizing (Kevin,
// 2026-08-19 overnight watchdog item 1): replaces `buildGrainAction`'s flat
// `uncommittedBu * 0.30/0.15` tranche formula, which sized off an
// ever-shrinking REMAINDER and therefore never converged — repeatedly taking
// 30% of what was left approaches but never reaches full protection even in
// principle. This file anchors the range on the farm's own CURRENT
// price-protected POSITION instead (grainCommitment.js's `priceProtectedBu`
// — the futures-direction-risk-closed bushels, not raw physical
// commitment), so a farm at 85% protected and STRONG reads a real, finite
// gap to 100%, not a fraction of 15% forever.
//
// STAGE RANGE, SOURCED (Kevin, 2026-08-19 review: "the current target-
// position model is just the old 15/30% tranche re-labeled... build a
// defensible target range by crop/crop-year/time of marketing year. Use a
// sourced policy, not an invented one."). grainMarketingStagePolicy.js now
// carries a REAL policy (CFFM/Usset's teaching marketing-plan framework —
// see that file's header for exactly what is and is not sourced from it),
// keyed off crop + cropYear + asOf. When it resolves (all three inputs
// present and the crop is one of the three CFFM has a plan for), its
// floor/ceiling band IS the stage range — this file no longer invents one.
// When it doesn't resolve (crop/cropYear/asOf missing, or a crop CFFM has no
// plan for), this file falls back to the ORIGINAL current-position-relative
// +15/+30 band, UNCHANGED from before this correction, labeled
// `policySource: "default-status-band"` so a caller can tell the two apart.
//
// market opportunity (the `status` grainAction.js's ladder already resolved)
// does NOT create the range — it only decides WHERE within the stage's own
// [floor, ceiling] band to place today's target (Kevin: "market opportunity
// may move the desired point within the range; storage/harvest pressure may
// affect urgency/optionality, but storage MUST NOT alter futures
// percentile"). WATCH/NO ACTION place at the range's floor (no push this
// cycle — wait); ATTRACTIVE places halfway to the ceiling; STRONG/URGENT
// place AT the ceiling (push all the way to what CFFM's own stage objective
// calls for). `status` is grainAction.js's own deterministic ladder (STATUS
// from that file) — this file takes it as a plain string so it never has to
// import grainAction.js (which will import THIS file), avoiding the same
// circular-import hazard grainCommitment.js's header documents.
//
// The FALLBACK band's widening magnitudes (15 / 30 percentage points) are
// NOT new weights — they are the exact fractions `buildGrainAction`'s prior
// tranche formula already used, unchanged from before this correction and
// used ONLY when no sourced stage policy resolves.
//
// PURE — no fetch, no React, no Supabase.
// ═══════════════════════════════════════════════════════════════════════════

import { roundDownToLot } from "./grainCash";
import { resolveMarketingStagePolicy } from "./grainMarketingStagePolicy";

const WIDEN_PCT_BY_STATUS = {
  "NO ACTION": 0,
  "WATCH": 0,
  "ATTRACTIVE": 15,
  "STRONG": 30,
  "URGENT": 30,
};

// STAGE_PLACEMENT_BY_STATUS — ONLY used when a sourced stage policy resolves
// (never in the fallback band, which keeps its own exact prior arithmetic).
// Expresses "how far from the stage floor toward the stage ceiling does
// today's market opportunity justify pushing" as a fraction of the
// [floor, ceiling] span — 0 = stay at the floor (wait), 1 = push all the way
// to the stage's own CFFM-sourced ceiling.
const STAGE_PLACEMENT_BY_STATUS = {
  "NO ACTION": 0,
  "WATCH": 0,
  "ATTRACTIVE": 0.5,
  "STRONG": 1,
  "URGENT": 1,
};

// resolveTargetPositionRange({ status, pctPriceProtected, crop, cropYear,
// asOf }) — the range itself, before translating to bushels.
// `pctPriceProtected` is grainCommitment.js's priceProtectedBu expressed as a
// % of expectedBu (0-100), or null when unknown (no grain_contracts rows on
// file, or no exposure denominator) — never coerced to 0.
//
// crop/cropYear/asOf are OPTIONAL and feed resolveMarketingStagePolicy(). All
// three must be real for a sourced stage to resolve — omitting any of them
// (or naming a crop CFFM has no plan for) falls back to the original
// current-position-relative +15/+30 band, fully backward compatible with
// every call site that predates this correction.
export function resolveTargetPositionRange({ status, pctPriceProtected, crop = null, cropYear = null, asOf = null } = {}) {
  if (pctPriceProtected == null) {
    return { known: false, reason: "current price-protected % is unknown — no target range without a starting position" };
  }
  const currentPct = Math.max(0, Math.min(100, pctPriceProtected));
  const stagePolicy = resolveMarketingStagePolicy({ crop, cropYear, asOf });

  let targetLow, targetHigh, floorPct, ceilingPct, stage, behindStageFloor, policySource, policyMethodologyRequired;
  if (stagePolicy.known && stagePolicy.range) {
    floorPct = stagePolicy.range.floorPct;
    ceilingPct = stagePolicy.range.ceilingPct;
    stage = stagePolicy.stage;
    behindStageFloor = currentPct < floorPct;
    policySource = stagePolicy.source.name;
    policyMethodologyRequired = false;
    // the low end of the RECOMMENDATION is never below the farm's own
    // current position (a recommendation can never ask a farm to walk
    // backward) — but it also can never be below the stage's own floor, so a
    // farm that is BEHIND the CFFM benchmark for this stage sees that floor,
    // not just "stay where you are".
    targetLow = Math.max(currentPct, floorPct);
    const placement = STAGE_PLACEMENT_BY_STATUS[status] ?? 0;
    targetHigh = Math.max(targetLow, Math.min(100, targetLow + placement * Math.max(0, ceilingPct - targetLow)));
  } else {
    // FALLBACK — unchanged from before this correction. Exact prior
    // arithmetic: targetLow = currentPct, targetHigh = currentPct + widen
    // (STRONG/URGENT reach the full 30-point widen, never a fraction of it).
    const widen = WIDEN_PCT_BY_STATUS[status] ?? 0;
    targetLow = currentPct;
    targetHigh = Math.max(targetLow, Math.min(100, currentPct + widen));
    floorPct = currentPct;
    ceilingPct = targetHigh;
    stage = null;
    behindStageFloor = false;
    policySource = "default-status-band";
    policyMethodologyRequired = true;
  }

  return {
    known: true, targetLow, targetHigh, currentPct,
    widenPct: +(targetHigh - targetLow).toFixed(2),
    status: status || null,
    stage, floorPct, ceilingPct, behindStageFloor,
    policySource, policyMethodologyRequired,
  };
}

// resolveGrainTargetGapBu — the GAP, in bushels, from CURRENT price-protected
// position to the range's HIGH end (the point market opportunity selected).
// Exact bushels are returned ONLY when both the position (priceProtectedBu)
// and the denominator (expectedBu) are credible — the caller (grainAction.js)
// is responsible for withholding this entirely when contract completeness is
// unknown (contractDataUnknown), matching the prior formula's own gating.
// Rounds DOWN to the nearest practical 1,000-bu lot — the SAME
// roundDownToLot() every other Grain sizing path uses, never a second
// rounding rule.
export function resolveGrainTargetGapBu({ status, pctPriceProtected, priceProtectedBu, expectedBu, crop = null, cropYear = null, asOf = null } = {}) {
  const range = resolveTargetPositionRange({ status, pctPriceProtected, crop, cropYear, asOf });
  if (!range.known || expectedBu == null || !(expectedBu > 0) || priceProtectedBu == null) {
    return { known: false, range, gapBuExact: null, gapBu: null, gapPct: null, targetBu: null };
  }
  const targetBu = (range.targetHigh / 100) * expectedBu;
  const gapBuExact = Math.max(0, targetBu - priceProtectedBu);
  const gapPct = +(range.targetHigh - range.currentPct).toFixed(1);
  return {
    known: true, range,
    gapBuExact,
    gapBu: roundDownToLot(Math.round(gapBuExact)),
    gapPct,
    targetBu,
  };
}
